import { useState } from "react";
import useTranslator from "../../../hooks/useTranslator";
import { getCurrentDate, makePriorLotNumber } from "../../../helpers/Utils";
import DateRangeFilter from "../../../components/shared/fields/DateRangeFilter";
import DashboardSection from "../../dashboard/components/DashboardSection";
import "../../dashboard/components/dashboard-sections.css";
import { MixingIconSvg } from "../../../svgFiles/MixingIconSvg";
import { PackageIconSvg } from "../../../svgFiles/PackageIconSvg";
import { useGetMixingSummaryQuery, useGetPastingSummaryQuery } from "../mixingApi";

const PastingDashboard = () => {
  const { _t } = useTranslator();
  const currentDate = getCurrentDate("YYYY-MM-DD");
  const [startDate, setStartDate] = useState(currentDate);
  const [endDate, setEndDate] = useState(currentDate);
  const [lotNumber, setLotNumber] = useState(makePriorLotNumber(currentDate));

  const formatValueWithUnit = (label, value) => {
    const displayValue = value ?? 0;
    const strValue = String(displayValue);

    if (/%/.test(label)) {
      return /%/.test(strValue) ? strValue : `${strValue} %`;
    }
    if (/per\s*kg|\/\s*kg/i.test(label)) {
      return /per\s*kg/i.test(strValue) ? strValue : `${strValue} Per KG`;
    }
    if (/\bkg\b|kilo|weight/i.test(label)) {
      return /\bkg\b/i.test(strValue) ? strValue : `${strValue} KG`;
    }
    return strValue;
  };

  const handleApplyRange = ({ startDate: start, endDate: end }) => {
    const single = start === end;
    setStartDate(start);
    setEndDate(end);
    if (single) {
      setLotNumber(makePriorLotNumber(start));
    }
  };

  // Mixing Summary Query
  const { data: mixingSummaryData } = useGetMixingSummaryQuery({ 
    start_date: startDate,
    end_date: endDate,
    batch_code: lotNumber 
  });

  // Pasting Summary Query
  const { data: pastingSummaryData } = useGetPastingSummaryQuery({ 
    start_date: startDate,
    end_date: endDate,
    batch_code: lotNumber 
  });

  // Mixing Overview Items - Color coded boxes
  const mixingListItems = [
    {
      label: _t("total_entered_weight", "Total Entered Weight"),
      value: mixingSummaryData?.data?.total_entered_weight ?? 0,
    },
    {
      label: _t("total_kg_from_our_lots", "Total KG from our lots"),
      value: mixingSummaryData?.data?.total_kg_from_lots ?? 0,
    },
    {
      label: _t("total_kg_from_brokerage", "Total KG From Brokerage"),
      value: mixingSummaryData?.data?.total_entered_from_brokerage ?? 0,
    },
  ];

  // Pasting Overview Items - Color coded boxes
  const pastingListItems = [
    {
      label: _t("total_kg_entered", "Total KG Entered"),
      value: pastingSummaryData?.data?.total_kg_entered ?? 0,
    },
    {
      label: _t("total_water_kg", "Total Water KG"),
      value: pastingSummaryData?.data?.total_water_kg ?? 0,
    },
    {
      label: _t("total_weight_after_mix", "Total Weight After Mix"),
      value: pastingSummaryData?.data?.total_weight_after_mix ?? 0,
    },
    {
      label: _t("packed_weight", "Weight Packed"),
      value: pastingSummaryData?.data?.packed_weight ?? 0,
    },
    {
      label: _t("unpacked_weight", "Weight Not Packed"),
      value: pastingSummaryData?.data?.unpacked_weight ?? 0,
    },
    {
      label: _t("total_boxes", "Total Boxes"),
      value: pastingSummaryData?.data?.total_boxes ?? 0,
    },
  ];

  return (
    <div>
      {/* ── Toolbar with Date & Lot Filter ──────────────────────── */}
      <div className="d-flex gap-2 align-items-center mb-4" style={{ justifyContent: "space-between" }}>
        <h5 className="m-0">{_t("pasting_mixing_dashboard", "Pasting & Mixing Dashboard")}</h5>
        <div className="d-flex gap-3 align-items-center">
          <div className="d-flex gap-2 align-items-center">
            <span className="fw-600" style={{ fontSize: "14px" }}>
              {_t("batch_code", "Batch Code")}:
            </span>
            <span
              style={{
                background: "#fff0ea",
                color: "#ee7225",
                padding: "6px 12px",
                borderRadius: "6px",
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              #{lotNumber ?? 0}
            </span>
          </div>
          <DateRangeFilter onApplyRange={handleApplyRange} triggerId="pasting-dashboard-date-range-filter" />
        </div>
      </div>

      <div>
        {/* ── Mixing Overview Accordion ──────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><MixingIconSvg size={18} color="#60a5fa" />{_t("mixing_overview", "Mixing Overview")}</span>}>
          <div className="pp-brokering-grid p-0">
            {mixingListItems.map((item, i) => (
              <div
                key={i}
                className="pp-brokering-box"
              >
                <div className="pp-brokering-box-label">
                  {item.label}
                </div>
                <div className="pp-brokering-box-value">
                  {formatValueWithUnit(item.label, item.value)}
                </div>
              </div>
            ))}
          </div>
        </DashboardSection>

        {/* ── Pasting Overview Accordion ──────────────────────────── */}
        <DashboardSection title={<span className="d-flex align-items-center gap-2"><PackageIconSvg size={18} color="#60a5fa" />{_t("pasting_overview", "Pasting Overview")}</span>}>
          <div className="db-trash-grid p-0" >
            {pastingListItems.map((item, i) => (
              <div
                key={i}
                className="db-trash-cell"
                style={{
                  borderRadius: "6px",
                }}
              >
                <span style={{ fontWeight: "500" }}>
                  {item.label}
                </span>
                <span
                  className="db-trash-val"
                  style={{ fontWeight: "600" }}
                >
                  {formatValueWithUnit(item.label, item.value)}
                </span>
              </div>
            ))}
          </div>
        </DashboardSection>
      </div>
    </div>
  );
};

export default PastingDashboard;
